import Dexie, { type Table } from "dexie";
import type { Attempt, Progress, Mastery } from "./types";

/**
 * Local-first storage. On the web this is IndexedDB (via Dexie); the same
 * repository API will back onto SQLite on desktop later, behind this boundary.
 */
class HippoDB extends Dexie {
  attempts!: Table<Attempt, number>;
  progress!: Table<Progress, string>;
  mastery!: Table<Mastery, string>;

  constructor() {
    super("hippo");
    this.version(1).stores({
      attempts: "++id, refType, refId, createdAt",
      progress: "id",
      mastery: "categoryId, lastPracticed",
    });
  }
}

export const db = new HippoDB();

const EMPTY_PROGRESS: Progress = {
  id: "singleton",
  xp: 0,
  level: 1,
  streakDays: 0,
  lastActiveDate: null,
  totalCompleted: 0,
};

/** XP needed to reach a given level (gentle curve). */
export function xpForLevel(level: number): number {
  return Math.round(100 * Math.pow(level, 1.4));
}

export function levelForXp(xp: number): number {
  let level = 1;
  while (xp >= xpForLevel(level + 1)) level++;
  return level;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a + "T00:00:00").getTime();
  const db_ = new Date(b + "T00:00:00").getTime();
  return Math.round((db_ - da) / 86_400_000);
}

export async function getProgress(): Promise<Progress> {
  const p = await db.progress.get("singleton");
  return p ?? EMPTY_PROGRESS;
}

export interface CompletionResult {
  progress: Progress;
  xpGained: number;
  leveledUp: boolean;
  streakIncreased: boolean;
}

/**
 * Records a completed attempt: persists it, awards XP, updates the daily streak
 * and category mastery, and returns the new progress with deltas for UI feedback.
 */
export async function recordCompletion(attempt: Attempt, xpGained: number): Promise<CompletionResult> {
  return db.transaction("rw", db.attempts, db.progress, db.mastery, async () => {
    await db.attempts.add(attempt);

    const prev = await getProgress();
    const today = todayStr();

    let streakDays = prev.streakDays;
    let streakIncreased = false;
    if (prev.lastActiveDate !== today) {
      if (prev.lastActiveDate && daysBetween(prev.lastActiveDate, today) === 1) {
        streakDays = prev.streakDays + 1;
      } else {
        streakDays = 1;
      }
      streakIncreased = true;
    }

    const xp = prev.xp + xpGained;
    const prevLevel = prev.level;
    const level = levelForXp(xp);

    const next: Progress = {
      id: "singleton",
      xp,
      level,
      streakDays,
      lastActiveDate: today,
      totalCompleted: prev.totalCompleted + 1,
    };
    await db.progress.put(next);

    // Update mastery per category touched.
    for (const catId of attempt.categoryIds) {
      const m = await db.mastery.get(catId);
      await db.mastery.put({
        categoryId: catId,
        attempts: (m?.attempts ?? 0) + 1,
        lastPracticed: attempt.createdAt,
      });
    }

    return {
      progress: next,
      xpGained,
      leveledUp: level > prevLevel,
      streakIncreased,
    };
  });
}

export async function getRecentAttempts(limit = 20): Promise<Attempt[]> {
  return db.attempts.orderBy("createdAt").reverse().limit(limit).toArray();
}

/** Every saved attempt, newest first. Backs the History view. */
export async function getAllAttempts(): Promise<Attempt[]> {
  return db.attempts.orderBy("createdAt").reverse().toArray();
}

export async function deleteAttempt(id: number): Promise<void> {
  await db.attempts.delete(id);
}

export async function getAllMastery(): Promise<Mastery[]> {
  return db.mastery.toArray();
}
